import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

export type ProductionPlanSearchStoreState = {
  searchResults: number[];
  searchSelectedTaskId: number | string | null;
};

export type ProductionPlanSearchStoreActions = {
  setSearchResults: (searchResults: number[]) => void;
  setSearchSelectedTaskId: (searchSelectedTaskId: number | string | null) => void;
  reset: () => void;
};

// Only one node can be search-selected (pulsing) at a time, so a single module-level
// timeout is enough to auto-clear it.
const SEARCH_SELECTED_PULSE_DURATION_MS = 5000;
let searchSelectedTimeoutId: NodeJS.Timeout | null = null;

export const useProductionPlanSearchStore = create(
  immer<ProductionPlanSearchStoreState & ProductionPlanSearchStoreActions>((set, get, store) => {
    return {
      searchResults: [],
      searchSelectedTaskId: null,
      setSearchResults: (searchResults) =>
        set((state) => {
          state.searchResults = searchResults;
        }),
      setSearchSelectedTaskId: (searchSelectedTaskId) => {
        if (searchSelectedTimeoutId) {
          clearTimeout(searchSelectedTimeoutId);
          searchSelectedTimeoutId = null;
        }

        set((state) => {
          state.searchSelectedTaskId = searchSelectedTaskId;
        });

        if (searchSelectedTaskId !== null) {
          searchSelectedTimeoutId = setTimeout(() => {
            set((state) => {
              state.searchSelectedTaskId = null;
            });
          }, SEARCH_SELECTED_PULSE_DURATION_MS);
        }
      },
      reset: () => {
        if (searchSelectedTimeoutId) {
          clearTimeout(searchSelectedTimeoutId);
          searchSelectedTimeoutId = null;
        }

        set(store.getInitialState());
      },
    };
  }),
);

export const isSearchEmphasizedSelector =
  (nodeIds: Array<number | string>) => (state: ProductionPlanSearchStoreState) =>
    state.searchResults.some((resultId) => nodeIds.some((nodeId) => String(nodeId) === String(resultId)));

export const isSearchSelectedSelector = (nodeIds: Array<number | string>) => (state: ProductionPlanSearchStoreState) =>
  state.searchSelectedTaskId !== null &&
  nodeIds.some((nodeId) => String(nodeId) === String(state.searchSelectedTaskId));
